import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Trash2, Eye, EyeOff, Globe, Radio } from 'lucide-react';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, 
  AlertDialogTitle, AlertDialogTrigger 
} from '../ui/alert-dialog';

export function AdminDJsTab({ 
  djs, 
  handleToggleDJFeatured,
  handleToggleDJVisibility,
  handleToggleDJLive,
  handleDeleteDJ,
  onViewDJ
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">DJ</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Location</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Genres</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Featured</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">
                <div className="flex items-center gap-1">
                  <Radio className="w-4 h-4 text-green-600" />
                  DJ Live
                </div>
              </th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {djs.map(dj => (
              <tr key={dj.dj_id} className={`border-b border-slate-100 hover:bg-slate-50 ${dj.is_hidden ? 'opacity-50' : ''}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div>
                      <p className="font-medium text-slate-900">{dj.stage_name || dj.name}</p>
                      {dj.stage_name && <p className="text-sm text-slate-500">{dj.name}</p>}
                      {dj.slug && <p className="text-xs text-slate-500 font-mono">/dj/{dj.slug}/live</p>}
                    </div>
                    {dj.is_hidden && (
                      <Badge variant="secondary" className="bg-red-100 text-red-700 text-xs">Hidden</Badge>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-slate-700">
                  {dj.location || [dj.location_city, dj.location_state].filter(Boolean).join(', ') || 'Location not specified'}
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {dj.genres?.slice(0, 2).map((genre, i) => (
                      <Badge key={i} variant="secondary" className="text-xs bg-violet-100 text-violet-800">
                        {genre}
                      </Badge>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <Switch
                    checked={dj.is_featured}
                    onCheckedChange={() => handleToggleDJFeatured(dj.dj_id, dj.is_featured)}
                    data-testid={`toggle-featured-dj-${dj.dj_id}`}
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={dj.dj_live_enabled || false}
                      onCheckedChange={() => handleToggleDJLive && handleToggleDJLive(dj.dj_id, dj.dj_live_enabled)}
                      data-testid={`toggle-dj-live-${dj.dj_id}`}
                      className="data-[state=checked]:bg-green-600"
                    />
                    {dj.has_active_session && (
                      <Badge className="bg-green-100 text-green-700 text-xs animate-pulse">LIVE</Badge>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleDJVisibility(dj.dj_id, dj.is_hidden)}
                      title={dj.is_hidden ? "Show DJ" : "Hide DJ"}
                      data-testid={`toggle-visibility-dj-${dj.dj_id}`}
                    >
                      {dj.is_hidden ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-slate-500" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDJ(dj.dj_id)}
                      data-testid={`view-dj-${dj.dj_id}`}
                    >
                      <Globe className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" data-testid={`delete-dj-${dj.dj_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete DJ</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{dj.stage_name || dj.name}"?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeleteDJ(dj.dj_id)} className="bg-red-600 hover:bg-red-700">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {djs.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          No DJs found
        </div>
      )}
    </div>
  );
}
